import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { Timestamp, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import AuthLayout from '../../components/auth/AuthLayout';
import OnboardingLoading, { ensureMinDuration } from '../../components/onboarding/OnboardingLoading';
import DailyLimitModal from '../../components/profile/DailyLimitModal';
import { auth, db } from '../../../firebaseConfig.js';
import { normalizeSocial } from '../../lib/validation/social';
import { triggerWelcomeEmail } from '../../lib/email/triggerWelcomeEmail';

const PLATFORM_OPTIONS = [
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'youtube', label: 'YouTube' },
];
// YouTube hidden from add flow until metrics API is available
const PLATFORM_OPTIONS_ADDABLE = PLATFORM_OPTIONS.filter((o) => o.value !== 'youtube');

const PLATFORM_PLACEHOLDERS = {
  tiktok: '@username or https://www.tiktok.com/@username',
  instagram: '@username or https://www.instagram.com/username',
  youtube: '@handle or channel URL',
};

const platformDomain = {
  tiktok: 'tiktok.com',
  instagram: 'instagram.com',
  youtube: 'youtube.com',
};

/** Secret handle to skip Apify scraper during onboarding (saves cost when testing). Use @skip (TikTok) or skip (Instagram). */
const ONBOARDING_SKIP_SCRAPER_HANDLE = 'skip';

const looksLikeUrl = (value) =>
  /^https?:\/\//i.test(value) || value.startsWith('www.');

const normalizePlatformValue = (value) => {
  if (!value) return '';
  const lower = value.toLowerCase();
  if (lower.includes('tiktok')) return 'tiktok';
  if (lower.includes('instagram')) return 'instagram';
  if (lower.includes('youtube')) return 'youtube';
  return '';
};

const Step3 = ({ onNavigate }) => {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  const [existingSocials, setExistingSocials] = useState([]);
  const [username, setUsername] = useState('');

  const [primaryPlatform, setPrimaryPlatform] = useState('');
  const [handleOrUrl, setHandleOrUrl] = useState('');

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scraperLoading, setScraperLoading] = useState(false);
  const [scraperDone, setScraperDone] = useState(false);
  const [scraperPlatform, setScraperPlatform] = useState('');
  const [dailyLimitModal, setDailyLimitModal] = useState({ open: false, nextAllowedAtISO: null, platform: '' });
  const dailyLimitHitRef = useRef(false);
  const [submitError, setSubmitError] = useState('');
  const [isFinishing, setIsFinishing] = useState(false);
  const [finishError, setFinishError] = useState('');
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [scraperErrorMessage, setScraperErrorMessage] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    if (!user) {
      onNavigate('login', null, { replace: true });
    }
  }, [authChecked, user, onNavigate]);

  useEffect(() => {
    if (!user || prefillLoaded) return;

    let isMounted = true;
    const loadProfile = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!isMounted) return;

        if (snap.exists()) {
          const data = snap.data() || {};
          if (data.username) {
            setUsername(data.username);
          } else if (data.usernameLower) {
            setUsername(data.usernameLower);
          }
          const socials = Array.isArray(data.socials) ? data.socials : [];
          setExistingSocials(socials);

          const primarySocial = socials.find((social) => social?.isPrimary) || socials[0];
          const platformFromSocial = normalizePlatformValue(primarySocial?.platform);
          const platformFromField = normalizePlatformValue(data.primaryPlatform);
          const resolvedPlatform = platformFromSocial || platformFromField;

          if (resolvedPlatform) {
            setPrimaryPlatform((prev) => prev || resolvedPlatform);
          }

          if (primarySocial?.url) {
            setHandleOrUrl((prev) => prev || primarySocial.url);
          } else if (primarySocial?.handle) {
            setHandleOrUrl((prev) => prev || `@${primarySocial.handle}`);
          } else if (data.primaryHandleOrUrl) {
            setHandleOrUrl((prev) => prev || data.primaryHandleOrUrl);
          }
        } else {
          setExistingSocials([]);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[onboarding] Failed to load social data', error);
        }
      } finally {
        if (isMounted) {
          setPrefillLoaded(true);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [user, prefillLoaded]);

  const placeholder = useMemo(() => {
    if (!primaryPlatform) return 'Enter your handle or profile link';
    return PLATFORM_PLACEHOLDERS[primaryPlatform] || 'Enter your handle or profile link';
  }, [primaryPlatform]);

  const getFieldErrors = () => {
    const errors = {};

    if (!primaryPlatform) {
      errors.primaryPlatform = 'Select a primary platform.';
    }

    if (!handleOrUrl.trim()) {
      errors.handleOrUrl = 'Handle or profile link is required.';
    }

    if (primaryPlatform && handleOrUrl.trim() && looksLikeUrl(handleOrUrl.trim())) {
      const expected = platformDomain[primaryPlatform];
      const normalizedUrl = handleOrUrl.trim().toLowerCase();
      const isYoutubeShort = primaryPlatform === 'youtube' && normalizedUrl.includes('youtu.be');
      if (!normalizedUrl.includes(expected) && !isYoutubeShort) {
        errors.handleOrUrl = `Please enter a valid ${PLATFORM_OPTIONS.find(option => option.value === primaryPlatform)?.label} URL.`;
      }
    }

    return errors;
  };

  // Placeholder enrichment function - resolves immediately
  // In the future, this can be replaced with actual enrichment logic
  const runEnrichment = async () => {
    // Placeholder: enrichment is queued in Firestore and will be processed separately
    // This function exists to allow for future async enrichment work
    return Promise.resolve();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitAttempted(true);
    setSubmitError('');
    setFinishError('');

    const errors = getFieldErrors();
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!user) {
      onNavigate('login');
      return;
    }

    // Start finishing flow - show loading screen
    setIsFinishing(true);
    const startTime = Date.now();
    setLoading(true);

    try {
      // Step 1: Save onboarding data (existing logic)
      const { handle, url } = normalizeSocial(primaryPlatform, handleOrUrl);
      const normalizedPlatform = primaryPlatform;

      const existingPlatform = (existingSocials || []).find(
        (social) => social?.platform === normalizedPlatform
      );
      const now = Timestamp.now();
      const newSocial = {
        platform: normalizedPlatform,
        handle: handle || null,
        url: url || '',
        isPrimary: true,
        source: 'onboarding',
        createdAt: existingPlatform?.createdAt || now,
        updatedAt: now,
      };

      const otherSocials = (existingSocials || [])
        .filter((social) => social?.platform !== normalizedPlatform)
        .map((social) => ({ ...social, isPrimary: false }));
      const socials = [...otherSocials, newSocial];

      await setDoc(
        doc(db, 'users', user.uid),
        {
          socials,
          primaryPlatform: normalizedPlatform,
          onboardingStatus: 'completed',
          onboardingCompleted: true,
          onboardingVersion: 1,
          onboardingCompletedAt: serverTimestamp(),
          onboarding: {
            step: 3,
            status: 'completed',
            completedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          enrichment: {
            status: 'queued',
            queuedAt: serverTimestamp(),
            lastAttemptAt: null,
            lastSuccessAt: null,
            error: null,
          },
        },
        { merge: true }
      );

      console.log('[signup] onboarding completed (Step3), triggering welcome email', { uid: user.uid });
      await triggerWelcomeEmail(user.uid);

      // Also sync socials and primaryPlatform to influencers collection (required before TikTok/Instagram refresh)
      try {
        const influencerRef = doc(db, 'influencers', user.uid);
        const influencerExisting = await getDoc(influencerRef);
        const existingInfluencer = influencerExisting.exists() ? influencerExisting.data() : {};
        const existingIdentity = existingInfluencer?.identity || {};

        // Build social links object from socials array
        const socialLinks = {};
        socials.forEach((social) => {
          if (social?.platform && social?.url) {
            socialLinks[social.platform] = social.url;
          }
        });

        const influencerData = {
          userId: user.uid,
          socials,
          primaryPlatform: normalizedPlatform,
          socialLinks,
          updatedAt: serverTimestamp(),
          identity: {
            ...existingIdentity,
          },
          socialConsent: { publicData: true },
          // Platform Performance visible by default for new users; preserve any existing section prefs
          sectionsEnabled: { ...(existingInfluencer?.sectionsEnabled || {}), platformPerformance: true },
        };

        // Preserve existing fields from previous steps
        if (existingInfluencer?.displayName) {
          influencerData.displayName = existingInfluencer.displayName;
          influencerData.identity.displayName = existingInfluencer.displayName;
        }
        if (existingInfluencer?.username) {
          influencerData.username = existingInfluencer.username;
          influencerData.identity.handle = existingInfluencer.username;
        }
        if (existingInfluencer?.contactEmail) {
          influencerData.contactEmail = existingInfluencer.contactEmail;
        }
        if (existingInfluencer?.photoURL) {
          influencerData.photoURL = existingInfluencer.photoURL;
          influencerData.profileImageUrl = existingInfluencer.profileImageUrl || existingInfluencer.photoURL;
          influencerData.avatarUrl = existingInfluencer.avatarUrl || existingInfluencer.photoURL;
          influencerData.identity.photoURL = existingInfluencer.photoURL;
        }

        // Preserve createdAt if exists
        if (existingInfluencer?.createdAt) {
          influencerData.createdAt = existingInfluencer.createdAt;
        } else {
          influencerData.createdAt = serverTimestamp();
        }

        await setDoc(influencerRef, influencerData, { merge: true });
      } catch (influencerError) {
        console.error('[onboarding] Failed to save to influencers collection:', influencerError?.message ?? influencerError);
      }

      // Gate refresh on actual influencer doc existence (not a boolean from setDoc)
      const influencerRefCheck = doc(db, 'influencers', user.uid);
      const influencerSnapCheck = await getDoc(influencerRefCheck);
      const canRefresh = influencerSnapCheck.exists();

      // Step 2: Run enrichment (placeholder - can be replaced with actual enrichment)
      try {
        await runEnrichment();
      } catch (enrichmentError) {
        // Log but don't block - enrichment failures shouldn't prevent onboarding completion
        console.error('[onboarding] Enrichment failed (non-blocking):', enrichmentError);
      }

      // Step 2b: TikTok auto-population via Apify (only when influencer doc exists)
      let scraperPhaseStartMs = null;
      if (!canRefresh) {
        console.warn('[onboarding] skipping refresh reason=influencer_doc_missing');
        setScraperDone(true);
      } else if (normalizedPlatform !== 'tiktok' || !(url || handle)) {
        const reason = normalizedPlatform !== 'tiktok' ? 'platform_not_tiktok' : 'no_tiktok_handle';
        console.warn('[onboarding] skipping refresh reason=' + reason);
        setScraperDone(true);
      } else {
        const tiktokHandleOrUrl = url || (handle ? `@${handle}` : handleOrUrl);
        const tiktokHandleNormalized = (tiktokHandleOrUrl || '').replace(/^@/, '').trim().toLowerCase();
        if (tiktokHandleNormalized === ONBOARDING_SKIP_SCRAPER_HANDLE) {
          console.warn('[onboarding] skipping TikTok refresh (test bypass handle)');
          setScraperDone(true);
        } else if (tiktokHandleOrUrl) {
          setScraperLoading(true);
          setScraperDone(false);
          setScraperPlatform('TikTok');
          scraperPhaseStartMs = Date.now();
          // Let the progress screen with dog paint before starting the API call
          await new Promise((r) => setTimeout(r, 250));
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000);
            const res = await fetch('/api/social/tiktok/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                tiktokHandleOrUrl,
                source: 'onboarding',
                userId: user.uid,
              }),
              signal: controller.signal,
            });
            clearTimeout(timeoutId);
            let tiktokBody = {};
            try {
              tiktokBody = await res.json();
            } catch (_) {}
            if (res.status === 429) {
              const errBody = tiktokBody;
              if (errBody?.error === 'DAILY_LIMIT') {
                dailyLimitHitRef.current = true;
                setDailyLimitModal({
                  open: true,
                  nextAllowedAtISO: errBody.nextAllowedAtISO ?? null,
                  platform: 'TikTok',
                });
              }
              console.warn('[onboarding] TikTok refresh failed (non-blocking):', res.status, errBody);
            } else if (!res.ok) {
              const errMsg = tiktokBody?.error || tiktokBody?.message || `HTTP ${res.status}`;
              const errorCode = tiktokBody?.code || tiktokBody?.errorCode;
              console.warn('[onboarding] TikTok refresh failed (non-blocking):', res.status, errMsg, { errorCode, ...tiktokBody });
              setScraperErrorMessage('We could not refresh TikTok data. Try again from your profile.');
            } else if (tiktokBody?.ok && (tiktokBody.account || tiktokBody.derived)) {
              try {
                const tiktok = {
                  handle: tiktokBody.account?.handle,
                  profileUrl: tiktokBody.account?.profileUrl,
                  followersCount: tiktokBody.account?.followersCount,
                  derived: tiktokBody.derived ?? null,
                  lastRefreshedAt: new Date().toISOString(),
                };
                sessionStorage.setItem('influencehub_onboarding_tiktok', JSON.stringify(tiktok));
              } catch (_) {}
            }
          } catch (tiktokErr) {
            console.warn('[onboarding] TikTok refresh failed (non-blocking):', tiktokErr?.message ?? tiktokErr);
            setScraperErrorMessage('We could not refresh TikTok data. Try again from your profile.');
          } finally {
            setScraperDone(true);
          }
        } else {
          console.warn('[onboarding] skipping refresh reason=no_tiktok_handle');
          setScraperDone(true);
        }
      }

      // Step 2c: Instagram auto-population via Apify (only when influencer doc exists)
      if (canRefresh && normalizedPlatform === 'instagram' && (url || handle)) {
        const igHandle = (handle || (url && url.match(/instagram\.com\/([^/?#]+)/i)?.[1]) || '')
          .replace(/^@/, '')
          .trim()
          .toLowerCase();
        if (igHandle === ONBOARDING_SKIP_SCRAPER_HANDLE) {
          console.warn('[onboarding] skipping Instagram refresh (test bypass handle)');
          setScraperDone(true);
        } else if (igHandle) {
          if (!scraperPhaseStartMs) scraperPhaseStartMs = Date.now();
          setScraperLoading(true);
          setScraperDone(false);
          setScraperPlatform('Instagram');
          await new Promise((r) => setTimeout(r, 250));
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000);
            const res = await fetch('/api/social/instagram/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                handle: igHandle,
                userId: user.uid,
              }),
              signal: controller.signal,
            });
            clearTimeout(timeoutId);
            let igBody = {};
            if (res.ok) {
              try {
                igBody = await res.json();
              } catch (_) {}
            }
            if (res.status === 429) {
              const errBody = igBody;
              if (errBody?.error === 'DAILY_LIMIT') {
                dailyLimitHitRef.current = true;
                setDailyLimitModal({
                  open: true,
                  nextAllowedAtISO: errBody.nextAllowedAtISO ?? null,
                  platform: 'Instagram',
                });
              }
              console.warn('[onboarding] Instagram refresh failed (non-blocking):', res.status, errBody);
            } else if (!res.ok) {
              console.warn('[onboarding] Instagram refresh failed (non-blocking):', res.status, igBody);
            } else if (igBody?.ok && igBody.instagram) {
              try {
                sessionStorage.setItem('influencehub_onboarding_instagram', JSON.stringify(igBody.instagram));
              } catch (_) {}
            }
          } catch (igErr) {
            console.warn('[onboarding] Instagram refresh failed (non-blocking):', igErr);
          } finally {
            setScraperDone(true);
          }
        }
      }

      // Step 3: Ensure minimum display time for overall flow (900ms) and for scraper phase (so user always sees dog)
      await ensureMinDuration(startTime, 900);
      if (scraperPhaseStartMs != null) {
        await ensureMinDuration(scraperPhaseStartMs, 2000);
      }

      // Step 4: Route directly to editable profile page (not public view) — skip if showing daily limit modal
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!dailyLimitHitRef.current) {
        onNavigate('profile-edit', null, { replace: true });
      }
    } catch (error) {
      console.error('[onboarding] Failed to save step 3', error);
      setFinishError('We could not save your social details. Please try again.');
      setIsFinishing(false);
      setSubmitError('We could not save your social details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // During onboarding finishing: single loading popup with progress 0→90 over ~12s, then 100 when scraper done
  useEffect(() => {
    if (!isFinishing || !scraperLoading) return;
    setOnboardingProgress(0);
    const start = Date.now();
    const durationMs = 12000;
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(90, (elapsed / durationMs) * 90);
      setOnboardingProgress(p);
      if (p >= 90) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, [isFinishing, scraperLoading]);

  useEffect(() => {
    if (scraperDone) setOnboardingProgress(100);
  }, [scraperDone]);

  const fieldErrors = getFieldErrors();

  if (!authChecked) {
    return (
      <AuthLayout
        title="Add your primary social"
        subtitle="Step 3 of 3 — Social presence"
        cardClassName="px-8 py-6"
      >
        <div className="text-center text-sm text-slate-500">Loading...</div>
      </AuthLayout>
    );
  }

  // Show single loading popup during finishing flow (no separate scraper modal; progress reflects scraper)
  if (isFinishing) {
    return (
      <>
        <OnboardingLoading
          label={scraperPlatform ? `Fetching ${scraperPlatform} stats...` : 'Setting up your profile'}
          progressPercent={onboardingProgress}
          errorMessage={scraperErrorMessage || undefined}
        />
        <DailyLimitModal
          isOpen={dailyLimitModal.open}
          onClose={() => {
            setDailyLimitModal((prev) => ({ ...prev, open: false }));
            dailyLimitHitRef.current = false;
            onNavigate('profile-edit', null, { replace: true });
          }}
          nextAllowedAtISO={dailyLimitModal.nextAllowedAtISO}
          platformLabel={dailyLimitModal.platform || 'this platform'}
        />
      </>
    );
  }

  return (
    <AuthLayout
      title="Add your primary social"
      subtitle="Step 3 of 3 — Social presence"
      cardClassName="px-8 py-6"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="primaryPlatform" className="text-sm font-medium text-slate-700">
            Primary Platform
          </label>
          <select
            id="primaryPlatform"
            name="primaryPlatform"
            value={primaryPlatform}
            onChange={(event) => setPrimaryPlatform(event.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
          >
            <option value="">Select a platform</option>
            {PLATFORM_OPTIONS_ADDABLE.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="mt-1 min-h-[18px] text-sm text-red-500">
            {submitAttempted && fieldErrors.primaryPlatform ? fieldErrors.primaryPlatform : ''}
          </div>
        </div>

        <div>
          <label htmlFor="handleOrUrl" className="text-sm font-medium text-slate-700">
            Handle or profile link
          </label>
          <input
            id="handleOrUrl"
            name="handleOrUrl"
            type="text"
            value={handleOrUrl}
            onChange={(event) => setHandleOrUrl(event.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            placeholder={placeholder}
            autoComplete="url"
          />
          <p className="mt-2 text-sm text-slate-500">
            We’ll use your public profile to auto-fill what we can.
          </p>
          <div className="mt-1 min-h-[18px] text-sm text-red-500">
            {submitAttempted && fieldErrors.handleOrUrl ? fieldErrors.handleOrUrl : ''}
          </div>
        </div>

        {submitError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {submitError}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className="h-12 w-full rounded-lg bg-[#1B9DA2] text-sm font-medium text-white transition hover:bg-[#168589] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Continue'}
        </button>

        <button
          type="button"
          onClick={() => onNavigate('onboarding/step-2')}
          className="w-full text-sm font-medium text-slate-500 transition hover:text-[#7842D6]"
        >
          Back
        </button>
      </form>
    </AuthLayout>
  );
};

export default Step3;
